<template>
  <div v-studio-click-outside="closeMenu" class="tw-relative">
    <StudioButton
      v-if="!isSlotEnabled"
      ref="studioDropdownTriggerButton"
      trackingId="studio-dropdown-trigger-button"
      :variant="variant"
      :dropdown="true"
      :class="extraTriggerButtonClasses"
      @click="toggleDropdownMenu"
    >
      {{ menuItems[selectedItemIndex].title }}
    </StudioButton>

    <slot v-else name="triggerButton" :setRef="setSlotRef" :toggleDropdownMenu="toggleDropdownMenu"></slot>

    <transition name="slide">
      <div
        v-if="isDropdownOpen"
        ref="dropdownContainer"
        data-testid="studio-dropdown-menu-list"
        :class="{
          'tw-z-50 tw-mt-1': true,
          'tw-absolute': !useFixedPosition,
          'tw-bottom-10 tw-left-0': !useFixedPosition && position === 'top-right',
          'tw-bottom-10 tw-right-0': !useFixedPosition && position === 'top-left',
          'tw-left-0': !useFixedPosition && (position === 'right' || position === 'bottom-right'),
          'tw-right-0': !useFixedPosition && (position === 'left' || position === 'bottom-left'),
          'tw-min-w-[8rem]': dropdownSize === 'small',
          'tw-w-64': dropdownSize === 'default',
          'tw-fixed': useFixedPosition,
          [extraDropdownClasses]: extraDropdownClasses
        }"
      >
        <div
          :class="{
            'tw-max-h-[400px] tw-overflow-auto tw-rounded-lg tw-border tw-bg-white tw-text-xs tw-font-medium tw-shadow-lg dark:tw-bg-black': true,
            [dropdownMaxHeight]: dropdownMaxHeight
          }"
        >
          <slot name="dropdownHeader"></slot>
          <div v-for="menuItem in menuItems.filter(item => !item.hidden)" :key="menuItem.id" class="tw-relative">
            <button
              :id="`item-${menuItem.id}`"
              :data-testid="`menu-item-${menuItem.title}`"
              :disabled="menuItem.disabled"
              :data-disabled-reason="menuItem.disabled && menuItem.disabledReason ? menuItem.disabledReason : undefined"
              :class="{
                'tw-group/menu-item tw-flex tw-w-full tw-items-center tw-gap-1': true,
                'tw-text-left tw-transition-colors': true,
                'hover:tw-bg-deep-orange-600 hover:tw-text-white': !menuItem.isHeader,
                'tw-bg-deep-orange-50 tw-text-deep-orange-600 dark:tw-border-slate-800 dark:tw-text-white':
                  !menuItem.isHeader && isItemSelected(menuItem) && showSelectedState,
                'tw-gap-1 tw-px-3 tw-py-3': dropdownSize === 'default',
                'tw-gap-1 tw-px-2 tw-py-2': dropdownSize === 'matchTrigger',
                'tw-px-2 tw-py-2': dropdownSize === 'small',
                'tw-cursor-not-allowed tw-opacity-50': menuItem.disabled,
                '-tw-mb-1 tw-mt-2 tw-cursor-default tw-font-normal tw-text-gray-400': menuItem.isHeader,
                [menuItem.extraClasses || '']: menuItem.extraClasses
              }"
              @click="menuItem.isHeader ? null : selectMenuItem($event, menuItem.id)"
              @mouseenter="setHoveredItem(menuItem.id)"
              @mouseleave="setHoveredItem(null)"
              @focus="setHoveredItem(menuItem.id)"
              @blur="setHoveredItem(null)"
            >
              <div v-if="menuItem.icon">
                <component :is="menuItem.icon" class="tw-h-4 tw-w-4" />
              </div>
              <div class="tw-flex tw-w-full tw-flex-col">
                <p
                  :class="{
                    'tw-break-words group-hover/menu-item:tw-text-white': true,
                    'tw-text-sm': !menuItem.isHeader,
                    'tw-text-xs': menuItem.isHeader,
                    [menuItem.titleExtraClasses || '']: menuItem.titleExtraClasses
                  }"
                >
                  {{ menuItem.title }}
                </p>
                <p
                  :class="{
                    'tw-break-words group-hover/menu-item:tw-text-white': true,
                    'tw-text-slate-500': true,
                    'tw-text-xs': menuItem.isHeader
                  }"
                >
                  {{ menuItem?.subTitle }}
                </p>
              </div>
            </button>
          </div>
          <div v-if="showNoItemsFound && menuItems.length === 0" class="tw-p-2">No items found</div>
        </div>
      </div>
    </transition>

    <StudioTooltip
      :show="hoveredItemId !== null && getHoveredMenuItem()?.disabled && !!getHoveredMenuItem()?.disabledReason"
      :text="getHoveredMenuItem()?.disabledReason || ''"
      :targetElement="(hoveredItemElement as Element) || undefined"
    />
  </div>
</template>

<script lang="ts">
import { Component, defineComponent, PropType, Ref } from 'vue';

import StudioButton, { StudioButtonVariant } from './StudioButton.vue';
import StudioTooltip from './StudioTooltip.vue';

export type MenuItem = {
  id: number;
  title: string;
  value?: string;
  subTitle?: string;
  hidden?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  titleExtraClasses?: string;
  extraClasses?: string;
  icon?: Component;
  isHeader?: boolean;
};

export default defineComponent({
  name: 'StudioDropdown',
  components: {
    StudioButton,
    StudioTooltip
  },
  props: {
    selectedItemIndex: {
      type: Number,
      default: 0
    },
    menuItems: {
      type: Array as PropType<MenuItem[]>,
      required: true
    },
    variant: {
      type: String as PropType<StudioButtonVariant>,
      default: 'filled'
    },
    dropdownSize: {
      type: String as PropType<'small' | 'default' | 'matchTrigger'>,
      default: 'default'
    },
    isSlotEnabled: {
      type: Boolean,
      default: false
    },
    position: {
      type: String as () =>
        | 'left'
        | 'right'
        | 'top-right'
        | 'top-left'
        | 'bottom-right'
        | 'bottom-left'
        | 'top'
        | 'bottom',
      default: 'right'
    },
    scrollToItemId: {
      type: Number as PropType<number | null>,
      default: null
    },
    useFixedPosition: {
      type: Boolean,
      default: false
    },
    extraTriggerButtonClasses: {
      type: String,
      default: ''
    },
    extraDropdownClasses: {
      type: String,
      default: ''
    },
    dropdownMaxHeight: {
      type: String,
      default: ''
    },
    showNoItemsFound: {
      type: Boolean,
      default: false
    },
    showSelectedState: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDropdownOpen: false,
      hoveredItemId: null as number | null,
      hoveredItemElement: null as HTMLElement | null,
      slotRef: null as Ref | null
    };
  },
  computed: {
    getHoveredMenuItem() {
      return () => {
        if (this.hoveredItemId === null) {
          return null;
        }
        return this.menuItems.find(item => item.id === this.hoveredItemId);
      };
    },
    selectedMenuItem() {
      return this.menuItems?.[this.selectedItemIndex] || null;
    }
  },
  watch: {
    isDropdownOpen(next: boolean) {
      if (!next) {
        // @ts-ignore
        this.$refs.studioDropdownTriggerButton?.$el?.blur();
      } else {
        this.$nextTick(() => {
          this.updateDropdownPosition();
        });
      }
    }
  },
  methods: {
    isItemSelected(menuItem: MenuItem): boolean {
      return this.selectedMenuItem?.id === menuItem.id;
    },
    setHoveredItem(itemId: number | null) {
      this.hoveredItemId = itemId;
      if (itemId !== null) {
        this.hoveredItemElement = document.getElementById(`item-${itemId}`) as HTMLElement;
      } else {
        this.hoveredItemElement = null;
      }
    },
    setSlotRef(ref: Ref) {
      this.slotRef = ref;
    },
    toggleDropdownMenu(e: Event) {
      e.preventDefault();
      e.stopPropagation();

      this.isDropdownOpen = !this.isDropdownOpen;
      if (this.scrollToItemId !== null) {
        this.$nextTick(() => {
          document.getElementById(`item-${this.scrollToItemId}`)?.scrollIntoView({ block: 'end', behavior: 'smooth' });
        });
      }

      if (this.dropdownSize === 'matchTrigger') {
        this.$nextTick(() => {
          this.adjustDropdownWidthToTrigger();
        });
      }
    },
    adjustDropdownWidthToTrigger() {
      const dropdownContainer = this.$refs.dropdownContainer as HTMLElement;
      if (!dropdownContainer) {
        return;
      }

      const triggerButton = this.isSlotEnabled
        ? // @ts-ignore
          (this.slotRef?.$el as HTMLElement)
        : // @ts-ignore
          (this.$refs.studioDropdownTriggerButton?.$el as HTMLElement);

      if (!triggerButton) {
        return;
      }

      dropdownContainer.style.width = `${triggerButton.offsetWidth}px`;
    },
    closeMenu() {
      this.isDropdownOpen = false;
    },
    selectMenuItem(e: Event, id: number) {
      e.preventDefault();
      e.stopPropagation();

      this.$emit('select', id);
      this.closeMenu();
    },
    updateDropdownPosition() {
      this.$nextTick(() => {
        const dropdownContainer = this.$refs.dropdownContainer as HTMLElement;
        if (!dropdownContainer) {
          return;
        }

        const screenWidth = window.innerWidth || document.documentElement.clientWidth;
        const screenHeight = window.innerHeight || document.documentElement.clientHeight;
        const dropdownRect = dropdownContainer.getBoundingClientRect();

        // Handle horizontal overflow
        if (dropdownRect.right > screenWidth) {
          dropdownContainer.style.right = '0px';
          dropdownContainer.style.left = 'auto';
        }

        // Handle vertical overflow
        if (dropdownRect.bottom > screenHeight) {
          dropdownContainer.style.bottom = '0px';
          dropdownContainer.style.top = 'auto';
        }

        if (this.useFixedPosition) {
          this.setDropdownStyleForFixedPosition();
        }
      });
    },
    setDropdownStyleForFixedPosition() {
      const dropdownContainer = this.$refs.dropdownContainer as HTMLElement;
      if (!dropdownContainer) {
        return;
      }

      const triggerButton = this.isSlotEnabled
        ? // @ts-ignore
          (this.slotRef?.$el as HTMLElement)
        : // @ts-ignore
          (this.$refs.studioDropdownTriggerButton?.$el as HTMLElement);

      if (!triggerButton) {
        return;
      }

      const triggerRect = triggerButton.getBoundingClientRect();
      const dropdownRect = dropdownContainer.getBoundingClientRect();
      const dropdownHeight = dropdownRect.height;
      const dropdownWidth = dropdownRect.width;
      const screenHeight = window.innerHeight;
      const screenWidth = window.innerWidth;
      const scrollY = window.scrollY;

      let position = this.position;

      // Auto-flip vertical position if needed
      if (
        (position === 'bottom' || position === 'bottom-left' || position === 'bottom-right') &&
        triggerRect.bottom + dropdownHeight > screenHeight
      ) {
        position = position.replace('bottom', 'top') as any;
      }
      if (
        (position === 'top' || position === 'top-left' || position === 'top-right') &&
        triggerRect.top - dropdownHeight < 0
      ) {
        position = position.replace('top', 'bottom') as any;
      }

      // Auto-flip horizontal position if needed
      if (position === 'right' && triggerRect.right + dropdownWidth > screenWidth) {
        position = 'left';
      }
      if ((position === 'bottom-right' || position === 'top-right') && triggerRect.right - dropdownWidth < 0) {
        position = position.replace('right', 'left') as any;
      }
      if (position === 'left' && triggerRect.left - dropdownWidth < 0) {
        position = 'right';
      }
      if ((position === 'bottom-left' || position === 'top-left') && triggerRect.left + dropdownWidth > screenWidth) {
        position = position.replace('left', 'right') as any;
      }

      // Apply consistent scroll offset to all positions
      const baseTop = triggerRect.top + scrollY;
      const baseLeft = triggerRect.left;
      const triggerRight = triggerRect.right;

      // Clear any existing positioning styles
      dropdownContainer.style.right = '';
      dropdownContainer.style.bottom = '';

      switch (position) {
        case 'top-left':
        case 'top':
          dropdownContainer.style.top = `${baseTop - dropdownHeight}px`;
          dropdownContainer.style.left = `${baseLeft}px`;
          break;
        case 'top-right':
          dropdownContainer.style.top = `${baseTop - dropdownHeight}px`;
          dropdownContainer.style.left = `${triggerRight - dropdownWidth}px`;
          break;
        case 'right':
          dropdownContainer.style.top = `${baseTop + triggerRect.height / 2 - dropdownHeight / 2}px`;
          dropdownContainer.style.left = `${triggerRight}px`;
          break;
        case 'left':
          dropdownContainer.style.top = `${baseTop + triggerRect.height / 2 - dropdownHeight / 2}px`;
          dropdownContainer.style.left = `${baseLeft - dropdownWidth}px`;
          break;
        case 'bottom-right':
          dropdownContainer.style.top = `${baseTop + triggerRect.height}px`;
          dropdownContainer.style.left = `${triggerRight - dropdownWidth}px`;
          break;
        case 'bottom-left':
        case 'bottom':
          dropdownContainer.style.top = `${baseTop + triggerRect.height}px`;
          dropdownContainer.style.left = `${baseLeft}px`;
          break;
      }
    }
  }
});
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.15s ease-in-out;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(10%);
}
</style>
